/*
 * gift-box-sync.js
 *
 * Charlie 2026-06-01:
 * Keeps the free Signature Gift Box line in sync with the total quantity of
 * UNIVERSAL LIVE PRODUCT (CHUUG) line items in the cart, GLOBALLY (not just
 * on the cart page). Runs after every cart mutation (add / change / update /
 * clear) and on DOMContentLoaded.
 *
 * Why: previously gift boxes were added only when the customer clicked the
 * "Secure Checkout" button. Express checkout buttons (Shop Pay, Apple Pay,
 * PayPal, Google Pay) bypass that JS, so orders shipped without their gift
 * boxes — bad UX, support tickets, refunds.
 *
 * Strategy:
 *   - Liquid bakes the list of qualifying variant IDs into the page as
 *     window.CHUUG_LIVE_VARIANT_IDS (array of ints).
 *   - Liquid bakes the giftbox variant ID into window.CHUUG_GIFTBOX_VARIANT_ID.
 *   - We monkey-patch window.fetch (alongside the topbar's existing patch) so
 *     any /cart/{add,change,update,clear}.js triggers a debounced sync.
 *   - We also intercept classic non-AJAX form posts to /cart/add (used by
 *     the PDP) so we can sync BEFORE the page navigates away.
 *
 * Sync logic:
 *   target  = sum of qty for cart lines whose variant_id ∈ CHUUG_LIVE_VARIANT_IDS
 *   current = qty of CHUUG_GIFTBOX_VARIANT_ID lines (sum across any duplicates)
 *   if target > current → POST /cart/add.js to add the diff
 *   if target < current → POST /cart/change.js to reduce to target (or remove)
 *   if target = current → no-op
 *
 * All giftbox lines carry properties: { "PERSONALISED COIN": "NONE" }
 * so the downstream fulfilment tagger doesn't flag the order Invalid_Name.
 *
 * Defensive: every fetch is try/catch'd; a sync failure logs and continues
 * so it can never block checkout.
 */
(function(){
  'use strict';

  // ── Config from Liquid ────────────────────────────────────────────────
  var LIVE_IDS = (window.CHUUG_LIVE_VARIANT_IDS || []).map(function(x){ return parseInt(x, 10); }).filter(Boolean);
  var GIFTBOX_ID = parseInt(window.CHUUG_GIFTBOX_VARIANT_ID, 10);

  if (!GIFTBOX_ID) {
    console.warn('[gift-sync] no giftbox variant ID configured — aborting');
    return;
  }
  if (!LIVE_IDS.length) {
    console.warn('[gift-sync] no live variant IDs configured — aborting');
    return;
  }

  // Fast lookup for "is this variant a qualifying live product?"
  var LIVE_SET = Object.create(null);
  LIVE_IDS.forEach(function(id){ LIVE_SET[id] = true; });

  // ── State ─────────────────────────────────────────────────────────────
  var syncing = false;        // single-flight lock
  var syncQueued = false;     // remember if a sync was requested mid-sync
  var debounceTimer = null;
  var DEBOUNCE_MS = 60;       // coalesce rapid mutations
  var inflightInternal = 0;   // count our own cart fetches so we don't recurse

  // Expose for debugging / manual trigger.
  //
  // beginExternalSwap() / endExternalSwap() let other cart code (e.g.
  // the personalisation-property × handler in main-cart.liquid) declare
  // "I'm running a multi-step cart mutation; please do not auto-sync
  // gift boxes between my steps and please do not reload the page."
  // Without this, gift-box-sync sees the intermediate "remove the only
  // CHUUG line" state, decides there are 0 CHUUGs, removes the gift
  // box too, and triggers its own location.reload() — racing the
  // external code's add.js follow-up. Charlie 2026-06-02.
  window.CHUUG_GIFTBOX_SYNC = {
    sync: scheduleSync,
    config: { liveIds: LIVE_IDS, giftboxId: GIFTBOX_ID },
    beginExternalSwap: function(){ inflightInternal++; },
    endExternalSwap:   function(){ inflightInternal = Math.max(0, inflightInternal - 1); },
    // Charlie 2026-06-11 PM dropdown-bug fix: lets external callers
    // probe whether a swap is in flight so they can skip their own
    // competing reloads.
    isExternalSwapBusy: function(){ return inflightInternal > 0; }
  };

  // ── Helpers ───────────────────────────────────────────────────────────
  function isInternalFetch(){ return inflightInternal > 0; }

  // Charlie 2026-06-29 (Audit Finding #2 — /cart.js storm dedupe):
  // Both gift-box-sync and medallion-orphan-sync hit /cart.js after every
  // mutation. Per BYC submit we previously generated ~6 GETs to /cart.js
  // back-to-back, which on slow / mobile / in-app-browser sessions tripped
  // Shopify's 429 rate limit and broke downstream sync. This shared helper
  // (also referenced by medallion-orphan-sync.js) collapses overlapping
  // in-flight /cart.js GETs into a single promise for a 250ms window.
  // CRITICAL: only GETs to /cart.js are cached. POSTs to /cart/add.js,
  // /cart/change.js, /cart/update.js, /cart/clear.js remain authoritative
  // and uncached — mutation responses must always reflect the latest cart.
  // Installed once on window so both sync scripts share the same cache.
  if (!window.CHUUG_CART_CACHE){
    window.CHUUG_CART_CACHE = (function(){
      var inflight = null;
      var expiresAt = 0;
      var WINDOW_MS = 250;
      function get(){
        var now = Date.now();
        if (inflight && now < expiresAt) return inflight;
        inflight = fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function(r){
            if (!r.ok) throw new Error('HTTP ' + r.status + ' on /cart.js');
            return r.json();
          });
        expiresAt = now + WINDOW_MS;
        inflight.catch(function(){}).then(function(){
          setTimeout(function(){
            if (Date.now() >= expiresAt) inflight = null;
          }, WINDOW_MS);
        });
        return inflight;
      }
      function bust(){ inflight = null; expiresAt = 0; }
      return { get: get, bust: bust };
    })();
  }

  function fetchJSON(url, init){
    // Route bare GETs of /cart.js through the shared dedupe cache (audit #2).
    var method = (init && init.method ? String(init.method).toUpperCase() : 'GET');
    if (method === 'GET' && /\/cart\.js(\?|$)/.test(url)){
      inflightInternal++;
      return window.CHUUG_CART_CACHE.get()
        .finally(function(){ inflightInternal--; });
    }
    inflightInternal++;
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { 'Accept': 'application/json' } }, init || {}))
      .then(function(r){
        if (!r.ok) throw new Error('HTTP ' + r.status + ' on ' + url);
        return r.json();
      })
      .finally(function(){ inflightInternal--; });
  }

  function postJSON(url, body){
    // Cart mutations invalidate the cached /cart.js GET so the next read
    // sees the post-mutation state instead of a 250ms-stale snapshot.
    if (window.CHUUG_CART_CACHE && /\/cart\/(add|change|update|clear)/.test(url)){
      try { window.CHUUG_CART_CACHE.bust(); } catch(_){}
    }
    return fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function dispatchCartUpdated(){
    try { document.dispatchEvent(new Event('cart:updated')); } catch(e){}
  }

  // Both the live giftbox variant ID and the legacy placeholder so we
  // can repair stale lines from either era. Mirrors main-cart.liquid's
  // GIFTBOX_VARIANT_IDS constant.
  var ALL_GIFTBOX_VIDS = { 55468959531387: 1, 56240425238907: 1 };
  if (GIFTBOX_ID) ALL_GIFTBOX_VIDS[GIFTBOX_ID] = 1;

  function countTargets(cart){
    var targetQty = 0;
    var giftboxQty = 0;
    var giftboxLineKey = null;
    var giftboxLineIdx = 0;
    (cart.items || []).forEach(function(it, i){
      var vid = it.variant_id;
      if (LIVE_SET[vid]) targetQty += it.quantity;
      if (vid === GIFTBOX_ID) {
        giftboxQty += it.quantity;
        if (!giftboxLineKey) {
          giftboxLineKey = it.key;       // preferred — survives line index shifts
          giftboxLineIdx = i + 1;        // fallback
        }
      }
    });
    return {
      target: targetQty,
      current: giftboxQty,
      lineKey: giftboxLineKey,
      lineIdx: giftboxLineIdx
    };
  }

  // Charlie 2026-06-11 PM (Mike Bug 2): repair any giftbox line whose
  // PERSONALISED COIN property is not 'NONE'. Stale carts pre-dating
  // the COIN convention (or carts mutated by an older code path) can
  // carry a real letter on the giftbox line — which then surfaces at
  // checkout as "Signature Gift Box and Tissue Wrap | PERSONALISED
  // COIN: E", misattributing the customer's vessel personalisation to
  // a giftbox. The fulfilment tagger also flags those lines Invalid_Name.
  //
  // Resolution: walk all giftbox-variant lines and patch any with a
  // non-'NONE' COIN value via /cart/change.js (id = line key,
  // properties = repaired bag). Idempotent: lines already at 'NONE'
  // are skipped. Sequenced (not parallel) to avoid /cart/change races.
  function repairGiftboxProperties(cart){
    var fixes = [];
    (cart.items || []).forEach(function(it){
      if (!ALL_GIFTBOX_VIDS[it.variant_id]) return;
      var props = it.properties || {};
      var coin = props['PERSONALISED COIN'];
      if (coin === 'NONE') return;
      // Build a clean property bag: start from existing, force COIN to
      // 'NONE'. Strip empty-string values so /cart/change.js doesn't
      // re-create them as visible empty rows in the admin.
      var repaired = {};
      Object.keys(props).forEach(function(k){
        if (props[k] != null && props[k] !== '') repaired[k] = props[k];
      });
      repaired['PERSONALISED COIN'] = 'NONE';
      fixes.push({ key: it.key, from: coin, properties: repaired });
    });
    if (!fixes.length) return Promise.resolve(0);
    console.info('[gift-sync] repairing ' + fixes.length + ' stale giftbox COIN line(s):',
      fixes.map(function(f){ return f.key + '=' + JSON.stringify(f.from); }).join(', '));
    // Sequence the changes so /cart/change.js sees a stable cart
    // between calls (Shopify serialises per-cart but we still want
    // deterministic ordering for logging).
    var p = Promise.resolve();
    fixes.forEach(function(f){
      p = p.then(function(){
        return postJSON('/cart/change.js', { id: f.key, properties: f.properties })
          .catch(function(err){
            console.warn('[gift-sync] giftbox prop repair failed for key', f.key, err);
          });
      });
    });
    return p.then(function(){ return fixes.length; });
  }

  // ── The actual sync ───────────────────────────────────────────────────
  function doSync(){
    if (syncing) { syncQueued = true; return Promise.resolve(); }
    syncing = true;
    syncQueued = false;

    return fetchJSON('/cart.js')
      .then(function(cart){
        // First repair any stale giftbox properties (Mike Bug 2). This
        // runs BEFORE the qty sync because a repair followed by a qty
        // adjustment in the same pass would double-mutate the cart;
        // serialise via .then so the second fetch sees the repaired state.
        return repairGiftboxProperties(cart).then(function(repairedCount){
          if (repairedCount > 0) {
            // Re-fetch so the qty step works on fresh state.
            return fetchJSON('/cart.js');
          }
          return cart;
        });
      })
      .then(function(cart){
        var s = countTargets(cart);

        if (s.target === s.current) {
          // Already in sync
          return null;
        }

        if (s.target > s.current) {
          var toAdd = s.target - s.current;
          console.info('[gift-sync] adjusting gift boxes from ' + s.current + ' to ' + s.target + ' (adding ' + toAdd + ')');
          return postJSON('/cart/add.js', {
            id: GIFTBOX_ID,
            quantity: toAdd,
            properties: { 'PERSONALISED COIN': 'NONE' }
          });
        }

        // target < current → reduce or remove
        console.info('[gift-sync] adjusting gift boxes from ' + s.current + ' to ' + s.target + ' (removing ' + (s.current - s.target) + ')');
        var body = { quantity: s.target };
        if (s.lineKey) body.id = s.lineKey;   // /cart/change.js accepts id=key
        else body.line = s.lineIdx;
        return postJSON('/cart/change.js', body);
      })
      .then(function(result){
        if (result) {
          dispatchCartUpdated();
          // Charlie 2026-06-11 PM (Mike P0 dropdown bug): same lock-aware
          // gating as medallion-orphan-sync. If an external cart-mutation
          // chain is in flight (e.g. main-cart.liquid's perso-prompt
          // dropdown handler is mid change.js → add.js → reload), don't
          // race it with our own reload — the external code's chain
          // gets aborted mid-flight and the customer's first click
          // silently fails. The external caller (which is holding the
          // lock via beginExternalSwap) will reload itself once its
          // chain completes.
          if (inflightInternal > 0) {
            console.info('[gift-sync] skipping reload — external swap in progress (inflightInternal=' + inflightInternal + ')');
            return result;
          }
          // If we mutated the cart while on the cart page, server-rendered
          // bits (giftbox banner count, line item HTML) are stale. Reload
          // so the banner shows the right "× N gift boxes included" count.
          // Guard against an infinite reload loop with sessionStorage.
          if (/^\/cart(\b|\/|$)/.test(window.location.pathname || '')) {
            try {
              var k = 'chuug-gift-sync-reloaded';
              if (!window.sessionStorage.getItem(k)) {
                window.sessionStorage.setItem(k, '1');
                setTimeout(function(){ window.location.reload(); }, 50);
              }
            } catch(e){
              setTimeout(function(){ window.location.reload(); }, 50);
            }
          }
        } else if (/^\/cart(\b|\/|$)/.test(window.location.pathname || '')) {
          // No mutation → clear the reload-guard so a future mutation
          // can reload again.
          try { window.sessionStorage.removeItem('chuug-gift-sync-reloaded'); } catch(e){}
        }
        return result;
      })
      .catch(function(err){
        console.warn('[gift-sync] sync failed:', err);
      })
      .finally(function(){
        syncing = false;
        if (syncQueued) setTimeout(scheduleSync, 0);
      });
  }

  function scheduleSync(){
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doSync, DEBOUNCE_MS);
  }

  // Expose for callers that need to AWAIT the sync (e.g. PDP form submit)
  function syncNow(){
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    return doSync().then(function(){
      // If another sync got queued while we ran, drain it too
      if (syncQueued) return doSync();
    });
  }
  window.CHUUG_GIFTBOX_SYNC.syncNow = syncNow;

  // ── Monkey-patch fetch to catch any cart mutation ─────────────────────
  // The topbar already has its own monkey-patch; ours coexists fine —
  // both layers wrap the original and both run their own logic.
  (function(){
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    var RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    window.fetch = function(input, init){
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch(e){}
      var p = origFetch.apply(this, arguments);
      // Bust the shared /cart.js dedupe cache after any cart mutation (audit #2).
      // Done from the fetch monkey-patch so we catch external POSTs to
      // /cart/*.js too, not just our own postJSON().
      if (url && RE.test(url) && window.CHUUG_CART_CACHE){
        p.then(function(){ try { window.CHUUG_CART_CACHE.bust(); } catch(_){} }, function(){});
      }
      if (!isInternalFetch() && url && RE.test(url)){
        p.then(function(){ scheduleSync(); }, function(){});
      }
      return p;
    };
  })();

  // ── Intercept classic <form action="/cart/add"> submits ───────────────
  // These bypass fetch entirely and navigate the page away — so we have to
  // intercept, POST via fetch ourselves, sync, then perform the redirect.
  // Skip forms that already use AJAX (look for a data-no-intercept hint).
  document.addEventListener('submit', function(e){
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.dataset.giftSyncIntercept === 'skip') return;
    var action = (form.getAttribute('action') || '').toLowerCase();
    if (!/\/cart\/add(\?|$|\.js)/.test(action)) return;
    // Only intercept if not already handled by something else (e.g. has
    // an onsubmit that returns false, or is being submitted via JS).
    if (form.dataset.giftSyncHandled === '1') return;

    e.preventDefault();
    form.dataset.giftSyncHandled = '1';

    var fd = new FormData(form);
    // /cart/add accepts form-urlencoded or multipart — FormData works.
    inflightInternal++;
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin', body: fd })
      .then(function(r){ inflightInternal--; if (!r.ok) throw r; return r.json(); })
      .then(function(){
        return syncNow();
      })
      .then(function(){
        // Navigate to cart (or wherever the form intended). If the form has
        // a `return_to` input we honour it; otherwise default to /cart.
        var rt = form.querySelector('input[name="return_to"]');
        var dest = (rt && rt.value) || '/cart';
        window.location.href = dest;
      })
      .catch(function(err){
        inflightInternal = Math.max(0, inflightInternal - 1);
        console.warn('[gift-sync] PDP form intercept failed, falling back to native submit:', err);
        // Reset flag so native submit isn't blocked
        form.dataset.giftSyncHandled = '';
        // Fire native submit; rely on cart page sync as the safety net
        form.submit();
      });
  }, true); // capture phase so we beat any other submit listeners

  // ── On page load, sync once ───────────────────────────────────────────
  // Catches "customer returned from elsewhere, cart is stale" + the
  // critical "cart page just rendered, express checkout button is sitting
  // right there" case.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
})();
